/**
 * Shared helper for the chat widget's "Quick Start Intro Message".
 *
 * Used by:
 * - Settings UI (compute the default fallback shown when no value has been saved yet)
 * - BFF chat-config aggregator (return a ready-to-render string to widgets)
 * - Chat widget WelcomeMessage (last-line defense if the BFF ever ships a null)
 *
 * Keeping the default in one place ensures the settings preview, the live widget,
 * and the persisted value all agree on what the "default" copy actually is.
 */

export const QUICK_START_INTRO_MAX = 250;

const FALLBACK_INSTITUTION_NAME = 'our school';

/**
 * Builds the default intro message, substituting the institution name into
 * the canonical template. When the institution name is missing, falls back
 * to a generic phrase so the copy still reads naturally.
 */
export function buildDefaultQuickStartIntro(
  institutionName: string | null | undefined,
): string {
  const name = (institutionName ?? '').trim() || FALLBACK_INSTITUTION_NAME;
  return `Hi there! Welcome to ${name}. I'm here to help with admissions, programs, and campus life. To give you the most relevant answers, can you tell me a bit more?`;
}

/**
 * Resolves the intro to render: prefers a saved (non-empty) value, otherwise
 * builds the default from the institution name.
 */
export function resolveQuickStartIntro(
  savedIntro: string | null | undefined,
  institutionName: string | null | undefined,
): string {
  const saved = (savedIntro ?? '').trim();
  if (saved) return saved;
  return buildDefaultQuickStartIntro(institutionName);
}
